import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n";
import { isIos, isStandalone } from "@/lib/pwa";

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const DISMISS_KEY = "bn_install_dismissed";

/**
 * Install-to-home-screen prompt. Android/Chrome uses the native
 * `beforeinstallprompt`; iOS Safari gets the Share → Add to Home Screen hint.
 */
export function InstallAppBanner() {
  const { t } = useTranslation("pwa");
  const [deferred, setDeferred] = useState<InstallEvent | null>(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isIos()) {
      const timer = window.setTimeout(() => setShowIos(true), 6000);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDeferred(null);
    setShowIos(false);
  };

  if (!deferred && !showIos) return null;

  return (
    <div
      role="region"
      aria-label={t("install.aria", "Install the BidNova app")}
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-2xl border border-white/10 bg-background/95 p-4 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5">
          <Download className="h-4 w-4 text-primary" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{t("install.title", "Install BidNova")}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {deferred
              ? t("install.body", "Add BidNova to your home screen for instant bidding and live alerts.")
              : t(
                  "install.iosBody",
                  "Tap Share, then “Add to Home Screen” to install BidNova for instant bidding and live alerts.",
                )}
          </p>
          <div className="mt-3 flex items-center gap-2">
            {deferred ? (
              <Button
                size="sm"
                onClick={async () => {
                  await deferred.prompt();
                  await deferred.userChoice.catch(() => null);
                  dismiss();
                }}
              >
                {t("install.action", "Install app")}
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Share className="h-3.5 w-3.5" aria-hidden /> {t("install.iosHint", "Share → Add to Home Screen")}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss}>
              {t("install.later", "Not now")}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("install.dismiss", "Dismiss install prompt")}
          className="rounded-md p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
